import { Formik, Form } from 'formik'
import noop from 'lodash/noop'
import { mergeRight } from 'ramda'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import * as Yup from 'yup'
import { Actions, Box, Button, Dialog, Stack, Textarea } from '@machobear/design-system'
import { Label } from '@/components/form'
import { Dropdown } from '@/components/ds-form'

const validationSchema = Yup.object().shape({
  user_message: Yup.string().min(1).max(500).required(),
  reference_type: Yup.string().required()
})

const ReportProblemContent = ({ data, onSave = noop, closeModal }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const initialValues = {
    user_message: '',
    user_id: data.user_ids.us[0],
    reference_type: ''
  }

  const MAP = {
    listing: data.listing_id,
    user: data.user_ids.them[0],
    building: data.buildings.id
  }

  const onSubmit = async (values, { setSubmitting }) => {
    const referenceId = MAP[values.reference_type]

    let updatedData = mergeRight(values, {
      reference_id: referenceId
    })

    const response = await dispatch.chat.reportProblem(updatedData)
    setSubmitting(false)

    if (!response.error) {
      onSave()
      closeModal()
    }
  }

  return (
    <Formik
      onSubmit={onSubmit}
      validationSchema={validationSchema}
      initialValues={initialValues}
      enableReinitialize
    >
      {({ values, setFieldValue, isSubmitting, handleSubmit }) => (
        <Form>
          <Stack space="small">
            <Dropdown
              name="reference_type"
              label={t(
                'b.chat.report_a_problem_modal.description',
                'What is the problem related to? '
              )}
              options={[
                {
                  value: 'listing',
                  label: t('b.chat.report_a_problem_modal_dropdown.listing.text', 'Listing')
                },
                {
                  value: 'user',
                  label: t('b.chat.report_a_problem_modal_dropdown.user.text', 'User')
                },
                {
                  value: 'building',
                  label: t('b.chat.report_a_problem_modal_dropdown.building.text', 'Building')
                },
                {
                  value: 'other',
                  label: t('common.other', 'Other')
                }
              ]}
              required
            />
            <Box>
              <Label id="user_message_label">
                {t(
                  'b.chat.report_a_problem_modal_describe_problem.label',
                  `Please describe the problem you're having`
                )}
              </Label>
              <Textarea
                name="user_message"
                id="user_message"
                aria-labelledby="user_message_label"
                characterLimit={500}
                value={values.user_message}
                onChange={e => setFieldValue('user_message', e.target.value)}
              />
            </Box>
          </Stack>
          <Box background="body" paddingTop="medium">
            <Actions align={'right'}>
              <Button
                type="submit"
                variant="solid"
                onClick={handleSubmit}
                size="small"
                disabled={isSubmitting}
              >
                {t('common.submit', 'Submit')}
              </Button>
            </Actions>
          </Box>
        </Form>
      )}
    </Formik>
  )
}

const ReportProblemModal = ({ data, open, onClose }) => {
  const { t } = useTranslation()

  return (
    <Dialog
      id="ReportProblemModal"
      title={t('b.chat.report_a_problem_modal.title', 'Report a Problem')}
      open={open}
      onClose={onClose}
    >
      <ReportProblemContent data={data} closeModal={onClose} />
    </Dialog>
  )
}

export default ReportProblemModal
